import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import apiClient from '../api/client';

const AuthCallback = () => {
  const [status, setStatus] = useState('processing'); // 'processing', 'success', 'error'
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const hasAttempted = useRef(false); // Prevent duplicate authentication attempts

  useEffect(() => {
    const handleAuthentication = async () => {
      // Prevent duplicate authentication attempts (React 18 Strict Mode issue)
      if (hasAttempted.current) {
        console.log('Authentication already attempted, skipping...');
        return;
      }
      hasAttempted.current = true;

      try {
        // Get the token from URL parameters
        const token = searchParams.get('token');
        const stytch_token_type = searchParams.get('stytch_token_type');
        const provider = searchParams.get('provider');
        const lead_id = searchParams.get('lead_id'); // For signup leads flow

        // Debug logging
        console.log('URL Search Params:', Object.fromEntries(searchParams.entries()));
        console.log('Token:', token);
        console.log('Stytch Token Type:', stytch_token_type);
        console.log('Provider:', provider);
        console.log('Lead ID:', lead_id);

        if (!token) {
          setError('No authentication token found in URL');
          setStatus('error');
          return;
        }

        // Check if this is a signup leads flow (has lead_id)
        if (lead_id) {
          console.log('Handling signup leads authentication flow');

          // Use the signup leads verification endpoint
          const response = await apiClient.post('/onboarding/verify-stytch-email', {
            token: token,
            lead_id: lead_id
          });

          if (response.data.success) {
            setStatus('success');

            // Redirect to onboarding wizard starting from step 1 (account setup)
            setTimeout(() => {
              navigate(`/onboarding?lead_id=${lead_id}&step=1`);
            }, 2000);
          } else {
            setError(response.data.message || 'Email verification failed');
            setStatus('error');
          }
        } else {
          console.log('Handling standard authentication flow');

          // Handle password reset tokens - redirect to password reset form instead of authenticating
          if (stytch_token_type === 'multi_tenant_passwords') {
            console.log('Password reset token detected, redirecting to reset password form');
            setStatus('success');

            // Store the token temporarily for the password reset form
            sessionStorage.setItem('password_reset_token', token);

            // Redirect to password reset form
            setTimeout(() => {
              navigate(`/auth/reset-password?token=${encodeURIComponent(token)}`);
            }, 1000);
            return;
          }

          // Determine the authentication endpoint based on token type
          let authEndpoint = 'auth/stytch/authenticate'; // Default to magic link (no leading slash)

          // If it's an OAuth token (including discovery_oauth), use the OAuth endpoint
          if ((stytch_token_type && stytch_token_type.includes('oauth')) || provider) {
            authEndpoint = 'auth/oauth/authenticate';
          }

          console.log('Using auth endpoint:', authEndpoint);

          // Send token to our backend for verification
          const response = await apiClient.post(authEndpoint, {
            token: token
          });

          if (response.data.success && response.data.access_token) {
            // Store the JWT token
            localStorage.setItem('auth_token', response.data.access_token);

            setStatus('success');

            // Redirect to dashboard after a brief delay
            setTimeout(() => {
              navigate('/calendar');
            }, 2000);
          } else {
            setError('Authentication failed');
            setStatus('error');
          }
        }
      } catch (err) {
        console.error('Authentication error:', err);

        // Handle B2B Discovery flow - this is expected for new organizations
        if (err.response?.status === 404 && err.response?.data?.error === 'discovery_flow_required') {
          console.log('Discovery flow required - handling B2B organization setup');

          const discoveryData = err.response.data;
          const intermediateToken = discoveryData.intermediate_session_token;
          const email = discoveryData.email_address;

          if (intermediateToken) {
            // Store the intermediate session token for profile completion
            localStorage.setItem('intermediate_session_token', intermediateToken);
            localStorage.setItem('discovery_email', email);

            // Get the lead_id for this email to continue the flow
            try {
              const leadResponse = await apiClient.get(`onboarding/lead-by-email?email=${encodeURIComponent(email)}`);
              if (leadResponse.data.success && leadResponse.data.lead_id) {
                const leadId = leadResponse.data.lead_id;

                setStatus('success');

                // Redirect to profile completion with lead_id
                setTimeout(() => {
                  navigate(`/onboarding?step=1&lead_id=${leadId}`);
                }, 2000);
                return;
              } else {
                // Fallback to discovery mode if lead not found
                setStatus('success');
                setTimeout(() => {
                  navigate('/onboarding?step=1&discovery=true');
                }, 2000);
                return;
              }
            } catch (leadErr) {
              console.warn('Could not fetch lead_id, proceeding with discovery mode:', leadErr);
              // Fallback to discovery mode
              setStatus('success');
              setTimeout(() => {
                navigate('/onboarding?step=1&discovery=true');
              }, 2000);
              return;
            }
          }
        }

        // Handle other errors - ensure error is always a string
        const errorMessage = err.response?.data?.message ||
          err.response?.data?.detail ||
          err.message ||
          'Authentication failed';

        setError(typeof errorMessage === 'string' ? errorMessage : 'Authentication failed');
        setStatus('error');
      }
    };

    handleAuthentication();
  }, [searchParams, navigate]);

  const handleRetry = () => {
    navigate('/login');
  };

  return (
    <div style={{
      maxWidth: 400,
      margin: '100px auto',
      padding: 20,
      textAlign: 'center'
    }}>
      <h2 style={{ marginBottom: 30 }}>MedInsight PRO</h2>

      {status === 'processing' && (
        <div>
          <div style={{
            fontSize: '48px',
            marginBottom: 20,
            animation: 'spin 1s linear infinite'
          }}>
            ⏳
          </div>
          <h3>Authenticating...</h3>
          <p style={{ color: '#666' }}>Please wait while we verify your login.</p>
        </div>
      )}

      {status === 'success' && (
        <div>
          <div style={{
            fontSize: '48px',
            marginBottom: 20,
            color: '#4caf50'
          }}>
            ✅
          </div>
          <h3 style={{ color: '#4caf50' }}>
            {searchParams.get('lead_id') ? 'Email Verified!' :
              searchParams.get('discovery') ? 'Email Verified!' :
                searchParams.get('stytch_token_type') === 'multi_tenant_passwords' ? 'Password Reset Link Verified!' :
                  'Login Successful!'}
          </h3>
          <p style={{ color: '#666' }}>
            {searchParams.get('lead_id')
              ? 'Redirecting to complete your account setup...'
              : searchParams.get('discovery')
                ? 'Redirecting to complete your clinic profile...'
                : searchParams.get('stytch_token_type') === 'multi_tenant_passwords'
                  ? 'Redirecting to password reset form...'
                  : 'Redirecting to your dashboard...'}
          </p>
        </div>
      )}

      {status === 'error' && (
        <div>
          <div style={{
            fontSize: '48px',
            marginBottom: 20,
            color: '#f44336'
          }}>
            ❌
          </div>
          <h3 style={{ color: '#f44336' }}>Authentication Failed</h3>
          <p style={{ color: '#666', marginBottom: 20 }}>{error}</p>

          <button
            onClick={handleRetry}
            style={{
              padding: '12px 24px',
              backgroundColor: '#3f51b5',
              color: 'white',
              border: 'none',
              borderRadius: 4,
              fontSize: '16px',
              cursor: 'pointer',
              fontWeight: 500
            }}
          >
            Try Again
          </button>
        </div>
      )}

      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default AuthCallback; 